/**
 * TopicDetailPanel — agent-rendered topic detail surface.
 *
 * Layout (top to bottom):
 *   1. Header — title + source chip + hypothesis block
 *   2. Timeline strip — transactions + key dates on a shared axis
 *   3. Entity chips — names / locations / merchants grouped by label
 *   4. Embedded TransactionAnalyzerPanel — drills into the transaction
 *      evidence using the existing single-rendering-path component
 *      (per /transaction-viewing skill).
 *
 * Read-only in Phase 2. Hypothesis edits and evidence add/remove are
 * driven by the agent through chat.
 *
 * Props (all sourced from the agent's render_topic_detail payload):
 *   topic_id: string
 *   title: string
 *   hypothesis: string
 *   description?: string
 *   source?: string
 *   transaction_ids: string[]
 *   transaction_summaries: { id, date, amount }[]   — paired with transaction_ids; for the timeline
 *   names: { name, role }[]
 *   locations: { name, role }[]
 *   merchants: { name, role }[]
 *   key_dates: { dateID, label, start_date, end_date, kind, confidence }[]
 *   onInteraction?: (event) => void
 */

import React from 'react';
import { Card, Badge, SectionTitle } from '../../design-system';
import TransactionAnalyzerPanel from './TransactionAnalyzerPanel';
import TopicTimeline from './TopicTimeline';
import styles from './TopicDetailPanel.module.css';
import { useTopicDetail } from '../../api/chat';

const SOURCE_LABEL = {
  interview: 'Interview',
  discovery: 'Discovery',
  ad_hoc: 'Ad hoc',
  merge: 'Merged',
  split: 'Split',
  seeded: 'Starter',
};

const ROLE_VARIANT = {
  supporting: 'success',
  contradicting: 'error',
  contextual: 'info',
};

function EntityChipGroup({ label, items }) {
  if (!items || items.length === 0) return null;
  return (
    <div className={styles.chipGroup}>
      <div className={styles.chipGroupLabel}>{label}</div>
      <div className={styles.chipRow}>
        {items.map((entity, idx) => (
          <span key={`${label}-${idx}`} className={styles.entityChip}>
            <span className={styles.entityName}>{entity.name}</span>
            <Badge variant={ROLE_VARIANT[entity.role] || 'default'} size="sm">
              {entity.role || 'supporting'}
            </Badge>
          </span>
        ))}
      </div>
    </div>
  );
}

// Phase 3f: human-readable role labels for the speaker-attribution chip.
// Mirrors the backend's case_role enum. Underscore-separated values get
// title-cased; we don't try to be cute with phrasing — the chip is meant
// to be clinical ("Captured by: Petitioner") not chatty.
const ROLE_LABEL = {
  petitioner: 'Petitioner',
  respondent: 'Respondent',
  child: 'Child',
  attorney: 'Attorney',
  paralegal: 'Paralegal',
  accountant: 'Accountant',
  consultant: 'Consultant',
  family_member: 'Family member',
  witness: 'Witness',
  person_of_interest: 'Person of interest',
  other: 'Other',
};

function TopicDetailPanel(props) {
  const { topic_id, onInteraction } = props;

  // Live-refresh: the agent's render payload is a point-in-time snapshot, so a
  // later headless DISCOVERY_DIG that attaches transactions / renames the topic
  // (in a different session) leaves this panel stale until manually refreshed.
  // Poll the current topic detail and prefer it; fall back to the agent-supplied
  // props for the first paint, and if the topic is gone (payload null) keep the
  // last props rather than blanking. claimant_userid is intentionally not used —
  // the role chip is enough visual context.
  const { data: liveData } = useTopicDetail(topic_id);
  const liveProps = liveData?.payload?.components?.[0]?.props;
  const d = liveProps || props;

  const {
    title,
    hypothesis,
    description,
    source,
    transaction_ids = [],
    transaction_summaries = [],
    names = [],
    locations = [],
    merchants = [],
    key_dates = [],
    claimant_role,
  } = d;

  const hasEntityChips = names.length > 0 || locations.length > 0 || merchants.length > 0;
  const claimantLabel = claimant_role ? (ROLE_LABEL[claimant_role] || claimant_role) : null;

  return (
    <div className={styles.panel}>
      {/* Header — hypothesis is the prominent visual */}
      <Card padding="lg">
        <div className={styles.headerRow}>
          <div className={styles.titleBlock}>
            <h2 className={styles.title}>{title}</h2>
            {description ? <div className={styles.description}>{description}</div> : null}
          </div>
          <div className={styles.headerBadges}>
            {claimantLabel ? (
              <Badge variant="default" size="sm" title="Speaker who introduced this topic">
                Captured by: {claimantLabel}
              </Badge>
            ) : null}
            {source ? (
              <Badge variant="default" size="sm">
                {SOURCE_LABEL[source] || source}
              </Badge>
            ) : null}
          </div>
        </div>
        {hypothesis ? (
          <blockquote className={styles.hypothesis}>{hypothesis}</blockquote>
        ) : null}
      </Card>

      {/* Timeline overlay */}
      {(transaction_summaries.length > 0 || key_dates.length > 0) ? (
        <Card padding="md">
          <SectionTitle>Timeline</SectionTitle>
          <TopicTimeline
            transaction_summaries={transaction_summaries}
            key_dates={key_dates}
          />
        </Card>
      ) : null}

      {/* Entity chips */}
      {hasEntityChips ? (
        <Card padding="md">
          <SectionTitle>People, places, and merchants</SectionTitle>
          <div className={styles.chipsBlock}>
            <EntityChipGroup label="Names" items={names} />
            <EntityChipGroup label="Locations" items={locations} />
            <EntityChipGroup label="Merchants" items={merchants} />
          </div>
        </Card>
      ) : null}

      {/* Embedded transaction analyzer — keyed on topic_id so a re-render
          with a different topic remounts the analyzer cleanly (matches the
          stability fix for stale filter chips in TransactionAnalyzerPanel). */}
      {transaction_ids.length > 0 ? (
        <div className={styles.analyzerSlot}>
          <TransactionAnalyzerPanel
            key={topic_id}
            title={`${title || 'Topic'} — supporting transactions`}
            transaction_ids={transaction_ids}
            onInteraction={onInteraction}
          />
        </div>
      ) : (
        <Card padding="md">
          <div className={styles.emptyHint}>
            No transactions linked to this topic yet. The agent can link them via
            chat — e.g., ask it to "link these to the topic."
          </div>
        </Card>
      )}
    </div>
  );
}

export default TopicDetailPanel;
